import { BLOCK_TAG_PATTERN } from '@/constants/regex';
import {
  TEXT_MEASUREMENT,
  TEXT_RENDERING_PROPERTIES,
} from '@/constants/string';

interface TruncationResult {
  content: string;
  isTruncated: boolean;
}

interface WordPositionInfo {
  text: string;
  startIndex: number;
  endIndex: number;
}

/**
 * Creates an off-screen measurement element with copied styles
 * @param containerElement - Source element to clone styles from
 * @returns Configured measurement element ready for use
 */
export const createMeasurementElement = (
  containerElement: HTMLElement,
): HTMLElement => {
  const measurementElement = containerElement.cloneNode(true) as HTMLElement;

  // Apply common measurement styles
  Object.assign(measurementElement.style, {
    position: 'absolute',
    visibility: 'hidden',
    top: TEXT_MEASUREMENT.OFFSCREEN_POSITION,
    left: TEXT_MEASUREMENT.OFFSCREEN_POSITION,
    width: `${containerElement.offsetWidth}px`,
  });

  // Copy text styles from original element
  const computedStyles = getComputedStyle(containerElement);
  const textStyles = TEXT_RENDERING_PROPERTIES.reduce<Record<string, string>>(
    (acc, prop) => {
      acc[prop] = computedStyles[prop];
      return acc;
    },
    {},
  );

  Object.assign(measurementElement.style, textStyles);

  return measurementElement;
};

/**
 * Checks if content fits within line constraints by comparing scroll vs client height
 * @param measurementElement - Element to measure
 * @returns True if content fits within line limit
 */
export const doesContentFit = (measurementElement: HTMLElement): boolean => {
  const { scrollHeight, clientHeight } = measurementElement;

  return (
    scrollHeight <= clientHeight + TEXT_MEASUREMENT.BROWSER_HEIGHT_TOLERANCE
  );
};

/**
 * Converts HTML to plain text by parsing DOM and normalizing whitespace
 * @param html - HTML string to convert
 * @returns Clean plain text without HTML tags
 */
export const htmlToPlainText = (html: string): string => {
  if (!html?.length) return '';

  // Parse HTML using DOMParser for safe text extraction
  const text = new DOMParser().parseFromString(html, 'text/html').body
    .textContent;

  // Normalize multiple whitespace characters to single space
  return text ? text.replace(/\s+/g, ' ').trim() : '';
};

/**
 * Converts HTML to plain text, injecting spaces at block-level boundaries
 * @param html - HTML string to convert
 * @returns Plain text with block boundaries replaced by spaces
 */
export const htmlToSpacedPlainText = (html: string): string => {
  if (!html) return '';

  const spaced = html
    .replace(BLOCK_TAG_PATTERN.BR, ' ')
    .replace(BLOCK_TAG_PATTERN.CLOSING, ' </$1>');

  const text = new DOMParser().parseFromString(spaced, 'text/html').body
    .textContent;

  return text?.replace(/\s+/g, ' ').trim() ?? '';
};

/**
 * Parses an HTML string into an array of plain-text bullet strings.
 * Priorities <li> elements; falls back to <p>/<div> blocks; then treats
 * the whole string as a single entry if no block structure is found.
 * @param html - Raw HTML string (typically rich-text editor output)
 * @returns Array of non-empty trimmed strings, each representing one bullet
 */
export const htmlToBulletLines = (html: string): string[] => {
  if (!html?.trim()) return [];

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const body = doc.body;

  // Priority 1: explicit list items (<li>)
  const listItems = Array.from(body.querySelectorAll('li'));
  if (listItems.length > 0) {
    return listItems
      .map((li) => li.textContent?.replace(/\s+/g, ' ').trim() ?? '')
      .filter(Boolean);
  }

  // Priority 2: paragraph or div blocks
  const blocks = Array.from(body.querySelectorAll('p, div'));
  if (blocks.length > 0) {
    return blocks
      .map((el) => el.textContent?.replace(/\s+/g, ' ').trim() ?? '')
      .filter(Boolean);
  }

  // Fallback: treat the entire content as one plain-text line
  const plain = htmlToSpacedPlainText(html);
  return plain ? [plain] : [];
};

/**
 * Determines if HTML content exceeds container height when line-clamped
 * @param containerElement - Container element for measurement context
 * @param htmlContent - HTML content to test for overflow
 * @param maxVisibleLines - Maximum lines before truncation (default: 4)
 * @returns True if content requires truncation to fit within line limit
 */
export const shouldTruncateContent = (
  containerElement: HTMLElement,
  htmlContent: string,
  maxVisibleLines = 4,
): boolean => {
  if (!containerElement || !htmlContent?.trim()) return false;

  // Create off-screen measurement element
  const measurementElement = createMeasurementElement(containerElement);
  document.body.appendChild(measurementElement);

  try {
    // Measure natural content height without line restrictions
    measurementElement.innerHTML = htmlContent;
    Object.assign(measurementElement.style, {
      display: 'block',
      overflow: 'visible',
    });
    const naturalContentHeight = measurementElement.scrollHeight;

    // Apply webkit line clamp and measure constrained height
    Object.assign(measurementElement.style, {
      display: '-webkit-box',
      webkitBoxOrient: 'vertical',
      webkitLineClamp: maxVisibleLines.toString(),
      overflow: 'hidden',
    });

    // Force browser reflow for accurate measurement
    void measurementElement.offsetHeight;

    // Compare heights with browser rendering tolerance
    const { clientHeight } = measurementElement;
    return (
      naturalContentHeight >
      clientHeight + TEXT_MEASUREMENT.BROWSER_HEIGHT_TOLERANCE
    );
  } finally {
    measurementElement.parentNode?.removeChild(measurementElement);
  }
};

/**
 * Creates line-clamped HTML content with expand button if truncation is needed
 * @param containerElement - Container element for measurement context
 * @param originalHtmlContent - Original HTML content to process
 * @param expandButtonElementId - Unique identifier for the expand button
 * @param maxVisibleLines - Maximum lines before truncation (default: 4)
 * @returns Object containing processed content and truncation status
 */
export const createLineBasedTruncatedContent = (
  containerElement: HTMLElement,
  originalHtmlContent: string,
  expandButtonElementId: string,
  maxVisibleLines = TEXT_MEASUREMENT.MAX_VISIBLE_LINES,
  isMobile = false,
): TruncationResult => {
  // Early validation for required inputs
  if (!containerElement || !originalHtmlContent?.trim()) {
    return { content: originalHtmlContent, isTruncated: false };
  }

  // Check if content needs truncation
  if (
    !shouldTruncateContent(
      containerElement,
      originalHtmlContent,
      maxVisibleLines,
    )
  ) {
    return { content: originalHtmlContent, isTruncated: false };
  }

  try {
    // Find optimal content length using binary search
    const truncatedContent = findOptimalContentLength(
      containerElement,
      originalHtmlContent,
      maxVisibleLines,
      expandButtonElementId,
      isMobile,
    );

    // Append expand button to truncated content
    const contentWithExpandButton = appendExpandButton(
      truncatedContent,
      expandButtonElementId,
      isMobile,
    );

    return {
      content: contentWithExpandButton,
      isTruncated: true,
    };
  } catch {
    // Graceful fallback on processing error
    return { content: originalHtmlContent, isTruncated: false };
  }
};

/**
 * Uses binary search to find optimal content length within line constraints
 * @param containerElement - Container element for measurement context
 * @param originalHtmlContent - Original HTML content to truncate
 * @param maxVisibleLines - Maximum allowed visible lines
 * @returns Optimally truncated HTML content that fits within line limit
 */
export const findOptimalContentLength = (
  containerElement: HTMLElement,
  originalHtmlContent: string,
  maxVisibleLines: number,
  expandButtonElementId: string,
  isMobile = false,
): string => {
  const MAX_ITERATIONS = 15;

  // Create off-screen measurement element
  const measurementElement = createMeasurementElement(containerElement);
  document.body.appendChild(measurementElement);

  try {
    // Quick check: if full content fits, return immediately
    measurementElement.innerHTML = originalHtmlContent;
    Object.assign(measurementElement.style, {
      display: '-webkit-box',
      webkitBoxOrient: 'vertical',
      webkitLineClamp: String(maxVisibleLines),
      overflow: 'hidden',
    });

    void measurementElement.offsetHeight;

    if (doesContentFit(measurementElement)) return originalHtmlContent;

    // Get word list to try cutting
    const wordPositions = extractWordPositions(originalHtmlContent);
    if (wordPositions.length === 0) return originalHtmlContent;

    let lowWordIndex = 0;
    let highWordIndex = wordPositions.length - 1;
    let optimalContent = '';
    let iterations = 0;

    // Binary search by word index
    while (lowWordIndex <= highWordIndex && iterations < MAX_ITERATIONS) {
      iterations++;
      const midWordIndex = Math.floor((lowWordIndex + highWordIndex) / 2);

      // Test content truncated at current word boundary
      const candidateContent = reconstructHtmlToWordBoundary(
        originalHtmlContent,
        wordPositions,
        midWordIndex,
      );

      measurementElement.innerHTML = appendExpandButton(
        candidateContent,
        expandButtonElementId,
        isMobile,
        true,
      );
      void measurementElement.offsetHeight;

      if (doesContentFit(measurementElement)) {
        optimalContent = candidateContent;
        lowWordIndex = midWordIndex + 1; // try longer
      } else {
        highWordIndex = midWordIndex - 1; // shorten
      }
    }

    return optimalContent;
  } finally {
    measurementElement.remove();
  }
};

/**
 * Extracts word positions from HTML content for precise text boundary detection
 * @param htmlContent - Original HTML content to analyze
 * @returns Array of words with their position metadata in the text
 */
export const extractWordPositions = (
  htmlContent: string,
): WordPositionInfo[] => {
  if (!htmlContent?.trim()) return [];

  const tempContainer = document.createElement('div');
  tempContainer.innerHTML = htmlContent;

  const wordPositions: WordPositionInfo[] = [];

  // Create tree walker to traverse only text nodes
  const textNodeWalker = document.createTreeWalker(
    tempContainer,
    NodeFilter.SHOW_TEXT,
  );

  let globalTextIndex = 0;

  let currentTextNode: Text | null;
  while ((currentTextNode = textNodeWalker.nextNode() as Text)) {
    // Split text by whitespace while preserving whitespace for accurate positioning
    const textSegments = (currentTextNode.textContent ?? '')
      .split(/(\s+)/)
      .filter(Boolean);

    for (const textSegment of textSegments) {
      const trimmedWord = textSegment.trim();

      if (trimmedWord.length > 0) {
        wordPositions.push({
          text: trimmedWord,
          startIndex: globalTextIndex,
          endIndex: globalTextIndex + trimmedWord.length,
        });
      }

      globalTextIndex += textSegment.length;
    }
  }

  return wordPositions;
};

/**
 * Reconstructs HTML content up to a specific word boundary
 * @param originalHtmlContent - Original HTML content to truncate
 * @param wordPositions - Array of word position metadata
 * @param targetWordIndex - Index of the last word to include (0-based)
 * @returns HTML content truncated at specified word boundary
 */
export const reconstructHtmlToWordBoundary = (
  originalHtmlContent: string,
  wordPositions: WordPositionInfo[],
  targetWordIndex: number,
): string => {
  if (targetWordIndex < 0 || targetWordIndex >= wordPositions.length) {
    return originalHtmlContent;
  }

  const plainText = htmlToPlainText(originalHtmlContent);
  if (!plainText) return originalHtmlContent;

  // Calculate precise text position by traversing words sequentially
  let currentTextPosition = 0;
  for (let wordIndex = 0; wordIndex <= targetWordIndex; wordIndex++) {
    const wordInfo = wordPositions[wordIndex];

    if (wordInfo) {
      const { text } = wordInfo;
      const wordPosition = plainText.indexOf(text, currentTextPosition);

      currentTextPosition = wordPosition + text.length;
    }
  }

  return truncateHtmlAtPosition(originalHtmlContent, currentTextPosition);
};

/**
 * Creates truncated HTML while preserving DOM structure and properly closing tags
 * @param originalHtmlContent - Original HTML content to truncate
 * @param maxChar - Maximum character length for text content
 * @returns Properly truncated HTML with valid DOM structure
 */
export const truncateHtmlAtPosition = (
  originalHtmlContent: string,
  maxChar: number,
): string => {
  if (maxChar <= 0) return '';

  const tempContainer = document.createElement('div');
  tempContainer.innerHTML = originalHtmlContent;

  let currentLength = 0;

  const truncateNodeRecursively = (currentNode: Node): boolean => {
    if (currentLength >= maxChar) return false;

    if (currentNode.nodeType === Node.TEXT_NODE) {
      const textContent = currentNode.textContent || '';
      const remainingChar = maxChar - currentLength;

      if (textContent.length <= remainingChar) {
        currentLength += textContent.length;
        return true;
      }

      // Truncate text node at character boundary
      const truncatedText = textContent.substring(0, remainingChar);

      currentNode.textContent = truncatedText;
      currentLength += truncatedText.length;
      return false;
    }

    if (currentNode.nodeType === Node.ELEMENT_NODE) {
      const elementNode = currentNode as Element;
      const childNodes = Array.from(elementNode.childNodes);

      for (let childIndex = 0; childIndex < childNodes.length; childIndex++) {
        const childNode = childNodes[childIndex];
        if (childNode && !truncateNodeRecursively(childNode)) {
          // Remove remaining siblings after truncation point
          const nodesToRemove = childNodes.slice(childIndex + 1);
          nodesToRemove.forEach((nodeToRemove) => {
            if (nodeToRemove) {
              elementNode.removeChild(nodeToRemove);
            }
          });
          return false;
        }
      }
      return currentLength < maxChar;
    }

    return true;
  };

  // Process all top-level container children
  const topLevelChildren = Array.from(tempContainer.childNodes);

  for (let childIndex = 0; childIndex < topLevelChildren.length; childIndex++) {
    const currentChild = topLevelChildren[childIndex];

    if (currentChild && !truncateNodeRecursively(currentChild)) {
      // Remove remaining top-level children after truncation
      const childrenToRemove = topLevelChildren.slice(childIndex + 1);

      childrenToRemove.forEach((childToRemove) => {
        if (childToRemove) {
          tempContainer.removeChild(childToRemove);
        }
      });
      break;
    }
  }

  return tempContainer.innerHTML;
};

/**
 * Appends expand button with ellipsis to truncated HTML content
 * @param truncatedHtmlContent - HTML content that has been truncated
 * @param expandButtonElementId - Unique DOM identifier for the expand button
 * @param isMobile - Whether this is for mobile display (affects affordance sizing)
 * @param isMeasurement - Whether the affordance is being rendered only for sizing
 * @returns HTML content with expand button and ellipsis
 */
export const appendExpandButton = (
  truncatedHtmlContent: string,
  expandButtonElementId: string,
  isMobile = false,
  isMeasurement = false,
): string => {
  if (!truncatedHtmlContent?.trim() || !expandButtonElementId) {
    return truncatedHtmlContent;
  }

  const tempContainer = document.createElement('div');
  tempContainer.innerHTML = truncatedHtmlContent;

  const findLastTextNode = (container: HTMLElement): Node | null => {
    let lastTextNode: Text | null = null;
    const textNodeWalker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_TEXT,
    );

    let currentTextNode: Text | null;
    while ((currentTextNode = textNodeWalker.nextNode() as Text | null)) {
      if (currentTextNode.textContent?.trim()) {
        lastTextNode = currentTextNode;
      }
    }
    return lastTextNode;
  };

  const createButtonMarkup = (
    buttonId: string,
    isMobile = false,
    isMeasurement = false,
  ): string => {
    const buttonSize = isMobile ? 'size-5' : 'size-6';
    const iconSize = isMobile ? '20' : '24';
    const baseButtonClasses = `inline-flex ${buttonSize} shrink-0 items-center justify-center align-middle bg-primary-750`;
    const elementTag = isMeasurement ? 'span' : 'button';
    const buttonAttributes = isMeasurement
      ? 'aria-hidden="true"'
      : `id="${buttonId}" type="button" aria-label="Expand to see more content" tabindex="0"`;

    return `
    <${elementTag}
      ${buttonAttributes}
      class="${baseButtonClasses}">
      <svg
        width="${iconSize}"
        height="${iconSize}"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        class="${buttonSize} rounded-sm cursor-pointer stroke-primary-700 text-primary-700"
        aria-hidden="true">
        <path
          d="M9.7 22.5h6c5 0 7-2 7-7v-6c0-5-2-7-7-7h-6c-5 0-7 2-7 7v6c0 5 2 7 7 7Z"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"/>
        <path
          d="M11.5 16.1l3.5-3.5-3.5-3.5"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"/>
      </svg>
    </${elementTag}>`;
  };

  // Keep ellipsis and expand button as one inline unit so they never split.
  const ellipsisContainer = document.createElement('span');
  ellipsisContainer.className =
    'inline-flex items-center gap-0.5 whitespace-nowrap align-middle';
  ellipsisContainer.innerHTML = `...${createButtonMarkup(
    expandButtonElementId,
    isMobile,
    isMeasurement,
  )}`;

  const lastTextNode = findLastTextNode(tempContainer);
  const targetParent = lastTextNode?.parentNode ?? tempContainer;
  targetParent.appendChild(ellipsisContainer);

  return tempContainer.innerHTML;
};
